import { Linkedin, Instagram, ArrowRight } from 'lucide-react'
import { Logo } from './Logo'
import { NewsletterSignup } from '@/components/ui/NewsletterSignup'
import { ExploreWithAi } from '@/components/ui/ExploreWithAi'
import { site } from '@/config/site'
import { Picture } from '@/components/ui/Picture'

// Simple X (Twitter) glyph — lucide dropped a maintained X mark.
function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z" />
    </svg>
  )
}

interface FooterLinkGroup {
  title: string
  links: { label: string; href: string }[]
}

const groups: FooterLinkGroup[] = [
  {
    title: 'About',
    links: [
      { label: 'Mission', href: '/#about' },
      { label: 'Leadership', href: '/#about' },
      { label: 'Advisory board', href: '/#about' },
      { label: 'Contact', href: '/#contact' },
    ],
  },
  {
    title: 'Our Work',
    links: [
      { label: 'Comprehensive LifeSpan', href: '/#work' },
      { label: 'Longevity Ecosystem', href: '/#ecosystem' },
      { label: 'LifeSpan Summit', href: site.summitUrl },
    ],
  },
  {
    title: 'Get involved',
    links: [
      { label: 'Partner with us', href: '/#contact' },
      { label: 'Donate', href: site.donateUrl },
      { label: 'Press & media', href: '/#media' },
    ],
  },
]

// Donor privacy folded into the Privacy Policy (Website Review V3).
const legalLinks = [
  { label: 'Privacy', href: '/privacy' },
  { label: 'Accessibility', href: '/accessibility' },
  { label: 'Terms', href: '/terms' },
]

export function Footer() {
  const year = new Date().getFullYear()
  return (
    <footer className="bg-ink-deep text-surface">
      {/* Top: subscribe + tagline band */}
      <div className="border-b border-surface/15">
        <div className="mx-auto grid max-w-[88rem] grid-cols-1 gap-12 px-6 py-16 sm:px-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="font-display text-3xl font-extrabold leading-[1.05] text-surface sm:text-4xl">
              A world built for longer lives.
            </p>
            {/* Photo under the headline (Website Review V3) */}
            <div className="mt-6 aspect-[16/9] max-w-md overflow-hidden rounded-sm">
              <Picture
                name="hero-thriving"
                sizes="(min-width: 640px) 448px, 100vw"
                alt="A joyful multigenerational group laughing together outdoors."
                className="h-full w-full object-cover object-[78%_center]"
              />
            </div>
          </div>
          <div className="lg:col-span-5">
            <h2 className="text-sm font-bold uppercase tracking-[0.14em] text-surface/70 sm:text-base">
              Subscribe
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-surface/80">
              Field notes on building a society for longer lives. Roughly
              monthly.
            </p>
            <div className="mt-5">
              <NewsletterSignup source="footer" variant="dark" />
            </div>
            {/* Opt-in language — TODO(client): final legal copy pending */}
            <p className="mt-4 text-xs leading-relaxed text-surface/60">
              By subscribing you agree to receive email updates from{' '}
              {site.name}. You can unsubscribe at any time.
            </p>
          </div>
        </div>
      </div>

      {/* Columnar links + social */}
      <div className="mx-auto grid max-w-[88rem] grid-cols-1 gap-y-12 px-6 py-14 sm:px-10 lg:grid-cols-12 lg:gap-x-10">
        <div className="lg:col-span-3">
          <Logo tone="light" />
          <ul className="mt-6 flex flex-wrap items-center gap-2" aria-label="Social">
            <SocialLink href={site.social.linkedin} label="LinkedIn" Icon={Linkedin} />
            <SocialLink href={site.social.x} label="X" Icon={XIcon} />
            <SocialLink href={site.social.instagram} label="Instagram" Icon={Instagram} />
            <li>
              <ExploreWithAi className="ml-1 border-surface/20 bg-transparent text-surface hover:bg-surface/10" />
            </li>
          </ul>
        </div>

        <div data-footer-links className="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 lg:col-span-6 lg:col-start-4">
          {groups.map((group) => (
            <nav key={group.title} aria-label={group.title}>
              <h2 className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-surface/60">
                {group.title}
              </h2>
              <ul className="mt-3 space-y-0.5">
                {group.links.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="inline-flex min-h-10 min-w-0 items-center gap-1 break-words text-sm font-medium text-surface/90 hover:text-gia-blue-light-2"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <div className="lg:col-span-3">
          <h2 className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-surface/60">
            Find resources
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-surface/80">
            Looking to connect with longevity programs in your region?
          </p>
          <a
            href="/#contact"
            className="mt-4 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.08em] text-surface hover:text-gia-blue-light-2"
          >
            Get in touch
            <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </a>
        </div>
      </div>

      {/* Compliance + legal */}
      <div className="border-t border-surface/15">
        <div className="mx-auto grid max-w-[88rem] grid-cols-1 gap-y-6 px-6 py-8 sm:px-10 lg:grid-cols-12 lg:items-center lg:gap-x-10">
          <p className="text-xs text-surface/65 lg:col-span-6">
            <strong className="font-bold text-surface">{site.name}</strong>{' '}
            is a 501(c)(3) nonprofit organization. EIN{' '}
            <span className="tabular">{site.ein}</span>.
          </p>
          <nav aria-label="Legal" className="lg:col-span-6">
            <ul className="flex flex-wrap items-center gap-x-5 gap-y-2 lg:justify-end">
              {legalLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="inline-flex min-h-10 items-center text-xs font-bold uppercase tracking-[0.1em] text-surface/80 hover:text-surface"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
              <li>
                <span className="text-xs text-surface/55">
                  &copy; {year}
                </span>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </footer>
  )
}

function SocialLink({
  href,
  label,
  Icon,
}: {
  href: string
  label: string
  Icon: React.ComponentType<{ className?: string }>
}) {
  return (
    <li>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${label} (opens in new tab)`}
        className="inline-flex h-9 w-9 items-center justify-center border border-surface/20 transition-colors duration-200 ease-out-quart hover:bg-surface hover:text-ink-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-surface focus-visible:ring-offset-2 focus-visible:ring-offset-ink-deep"
      >
        <Icon className="h-4 w-4" aria-hidden />
      </a>
    </li>
  )
}
